/**
 * ResumeCard — "Continue where you left off" tile. Surfaces the single
 * most-recent meaningful activity (last lecture, last SRS review, last
 * Study Buddy chat) from /dashboard/resume.
 *
 * Renders nothing for brand-new accounts (no `item`) so it doesn't add
 * noise to the empty Day-0 dashboard.
 */
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCap, BookMarked, Brain, ArrowRight } from 'lucide-react';
import { useResumeItem } from '../../hooks/useHeroContext';

const ICON_MAP = {
  lecture: GraduationCap,
  review: BookMarked,
  study_buddy: Brain,
};

const TINT_MAP = {
  lecture: { bg: 'bg-blue-50', icon: 'text-[#002FA7]', accent: 'border-[#002FA7]/20' },
  review: { bg: 'bg-violet-50', icon: 'text-violet-600', accent: 'border-violet-200' },
  study_buddy: { bg: 'bg-emerald-50', icon: 'text-emerald-600', accent: 'border-emerald-200' },
};

const ResumeCard = () => {
  const { data, isLoading } = useResumeItem();
  if (isLoading || !data?.item) return null;
  const item = data.item;
  const Icon = ICON_MAP[item.type] || GraduationCap;
  const tint = TINT_MAP[item.type] || TINT_MAP.lecture;

  return (
    <Link to={item.url} data-testid="resume-card">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className={`rounded-2xl border ${tint.accent} bg-[var(--isp-page)] hover:shadow-md transition-all p-4 sm:p-5 flex items-center gap-4 group mb-4`}
      >
        <div className={`w-12 h-12 rounded-xl ${tint.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-6 h-6 ${tint.icon}`} aria-hidden />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-[var(--isp-muted)]">
            Continue where you left off · {item.ago}
          </p>
          <h3 className="font-heading text-base sm:text-lg font-semibold text-[var(--isp-heading)] truncate mt-0.5">
            {item.title}
          </h3>
          <p className="text-xs text-[var(--isp-body)] truncate">{item.subtitle}</p>
        </div>
        <div className={`hidden sm:flex items-center gap-1.5 text-sm font-semibold ${tint.icon} whitespace-nowrap`}>
          {item.cta}
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" aria-hidden />
        </div>
      </motion.div>
    </Link>
  );
};

export default ResumeCard;
